import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  viewChild,
  inject,
  DestroyRef,
  afterNextRender,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  targetAlpha: number;
  twinkleSpeed: number;
  vx: number;
  vy: number;
}

interface DustParticle {
  x: number;
  y: number;
  radius: number;
  color: string;
  vx: number;
  vy: number;
  alpha: number;
}

interface ConstellationNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
}

@Component({
  selector: 'app-cosmic-canvas',
  standalone: true,
  templateUrl: './cosmic-canvas.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block absolute inset-0 pointer-events-none overflow-hidden',
  },
})
export class CosmicCanvas {
  readonly canvasRef =
    viewChild<ElementRef<HTMLCanvasElement>>('canvasElement');

  private animationFrameId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private stars: Star[] = [];
  private dustParticles: DustParticle[] = [];
  private constellationNodes: ConstellationNode[] = [];

  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      this.initCanvas();
    });

    this.destroyRef.onDestroy(() => {
      if (isPlatformBrowser(this.platformId)) {
        if (this.animationFrameId !== null) {
          cancelAnimationFrame(this.animationFrameId);
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }
    });
  }

  private initCanvas(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
        this.populateParticles(canvas.width, canvas.height);
      }
    };

    resize();

    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      this.resizeObserver = new ResizeObserver(() => resize());
      this.resizeObserver.observe(canvas.parentElement);
    }

    const render = () => {
      this.draw(ctx, canvas.width, canvas.height);
      this.animationFrameId = requestAnimationFrame(render);
    };

    render();
  }

  private populateParticles(width: number, height: number): void {
    const starCount = Math.min(Math.floor((width * height) / 8000), 120);
    this.stars = Array.from({ length: starCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 1.5 + 0.5,
      alpha: Math.random(),
      targetAlpha: Math.random(),
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      vx: (Math.random() - 0.5) * 0.15,
      vy: (Math.random() - 0.5) * 0.15,
    }));

    const dustCount = Math.min(Math.floor((width * height) / 25000), 15);
    const colors = [
      'rgba(102, 87, 236, ',
      'rgba(219, 39, 119, ',
      'rgba(56, 189, 248, ',
    ];
    this.dustParticles = Array.from({ length: dustCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 80 + 40,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      alpha: Math.random() * 0.15 + 0.05,
    }));

    const nodeCount = Math.min(Math.floor((width * height) / 30000), 18);
    this.constellationNodes = Array.from({ length: nodeCount }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 2 + 1,
    }));
  }

  private draw(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): void {
    ctx.clearRect(0, 0, width, height);

    // Draw cosmic dust clouds
    for (const dust of this.dustParticles) {
      dust.x += dust.vx;
      dust.y += dust.vy;
      if (dust.x < -dust.radius) dust.x = width + dust.radius;
      if (dust.x > width + dust.radius) dust.x = -dust.radius;
      if (dust.y < -dust.radius) dust.y = height + dust.radius;
      if (dust.y > height + dust.radius) dust.y = -dust.radius;

      const grad = ctx.createRadialGradient(
        dust.x,
        dust.y,
        0,
        dust.x,
        dust.y,
        dust.radius,
      );
      grad.addColorStop(0, dust.color + dust.alpha + ')');
      grad.addColorStop(1, dust.color + '0)');

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(dust.x, dust.y, dust.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw stars
    for (const star of this.stars) {
      star.x += star.vx;
      star.y += star.vy;
      if (star.x < 0) star.x = width;
      if (star.x > width) star.x = 0;
      if (star.y < 0) star.y = height;
      if (star.y > height) star.y = 0;

      if (Math.abs(star.alpha - star.targetAlpha) < 0.01) {
        star.targetAlpha = Math.random();
      }
      star.alpha += (star.targetAlpha - star.alpha) * star.twinkleSpeed;

      ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw constellation nodes & lines
    for (let i = 0; i < this.constellationNodes.length; i++) {
      const node = this.constellationNodes[i];
      node.x += node.vx;
      node.y += node.vy;
      if (node.x < 0 || node.x > width) node.vx *= -1;
      if (node.y < 0 || node.y > height) node.vy *= -1;

      ctx.fillStyle = 'rgba(102, 87, 236, 0.6)';
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fill();

      for (let j = i + 1; j < this.constellationNodes.length; j++) {
        const other = this.constellationNodes[j];
        const dx = other.x - node.x;
        const dy = other.y - node.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 140) {
          const lineAlpha = (1 - dist / 140) * 0.25;
          ctx.strokeStyle = `rgba(102, 87, 236, ${lineAlpha})`;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(other.x, other.y);
          ctx.stroke();
        }
      }
    }
  }
}
